import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { map, Observable, shareReplay, tap } from 'rxjs';
import ProjectLinks from 'src/app/common/project.links';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { PostBasic, UserSocialGroupsStats } from 'src/app/swagger-api';
import { CardType } from 'src/app/views/cards/cards.types';
import {
  DEFAULT_SORT_OPTIONS,
  SortCategories,
  SortOptions,
} from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import {
  INITIAL_PAGINATION,
  ListController,
  ListMode,
  ListState,
  sortParams,
} from '../../shared/list-controller/list-controller';
import { PostsService } from '../posts.service';

@Component({
  selector: 'app-user-posts',
  templateUrl: './user-posts.component.html',
  styleUrls: ['./user-posts.component.scss'],
})
export class UserPostsComponent implements OnInit {
  public readonly ListMode: typeof ListMode = ListMode;
  public readonly CardType: typeof CardType = CardType;

  public readonly SORT_OPTIONS: SortOptions[] = DEFAULT_SORT_OPTIONS;

  public readonly SEARCH_INPUT_LABEL = 'SEARCH_POSTS_FIELD_LABEL';

  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private postsService = inject(PostsService);
  private notificationService = inject(NotificationService);
  private operationService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  private allowedCreatePost = false;

  private readonly page = new ListController<'', string>({
    context: '',
    filtering: '',
    queryKeyword: '',
    mode: ListMode.BROWSE,
    pagination: INITIAL_PAGINATION,
    sorting: SortCategories.SCORE_DESC,
  });

  public readonly page$ = this.page.state$.pipe(shareReplay(1));

  public readonly sortParams$ = this.page.state$.pipe(map((page: ListState<'', string>) => sortParams[page.sorting]));
  public readonly isLoading$ = this.operationService.isLoadingLocallSpinner$;

  public readonly posts$: Observable<PostBasic[]> = this.postsService.postsChanges.pipe(
    map(({ items, total_count }) => {
      this.page.updateCount(total_count);
      return items;
    }),
  );

  public readonly searchPosts$: Observable<PostBasic[]> = this.postsService.searchPostsChanges.pipe(
    map(({ items, total_count }) => {
      this.page.updateCount(total_count);
      return items;
    }),
  );

  public ngOnInit(): void {
    this.postsService.fetchRequestSocialGroups();

    this.postsService.socialUserGroupsChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ total_communities, total_projects }: UserSocialGroupsStats) => {
          this.allowedCreatePost = total_communities > 0 || total_projects > 0;
        }),
      )
      .subscribe();

    this.page.params$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params: ListState<'', string>) => {
          const { pagination, sorting, mode, queryKeyword } = params;

          mode === ListMode.BROWSE
            ? this.postsService.fetchCurrentUserPosts({ pagination })
            : this.postsService.searchPosts(queryKeyword, pagination, sorting);
        }),
      )
      .subscribe();
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.page.patch({ sorting: sortOptions.sortCategory });
  }

  public onKeywordChanged(keyword: string): void {
    const { snapshot } = this.page;
    const { pagination, filtering, queryKeyword } = this.page.initialState;

    const state: ListState<'', string> = {
      ...snapshot,
      pagination,
      filtering,
      queryKeyword,
      sorting: SortCategories.SCORE_DESC,
      mode: ListMode.SEARCH,
    };

    const _keyword: string = keyword.trim();

    if (snapshot.mode === ListMode.BROWSE && _keyword) {
      this.page.patch({ ...state, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && _keyword) {
      this.page.patch({ ...snapshot, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && !_keyword) {
      this.page.patch({ ...state, mode: ListMode.BROWSE, queryKeyword: '' });
      return;
    }
  }

  public createPost(): void {
    if (!this.allowedCreatePost) {
      this.notificationService.dialogDefault({
        title: 'POSTS_REFUSE_TO_CREATE_TITLE',
        description: 'POSTS_REFUSE_TO_CREATE_DESCRIPTION',
        buttons: this.modalAttributesService.getDefaultCloseAttr(),
      });

      return;
    }

    promiseWithCatch(this.router.navigate([ProjectLinks.LINK_POSTS_CREATE]));
  }

  public onPageChanged(currentPage: number): void {
    const { pagination } = this.page;

    this.page.patch({ pagination: { ...pagination, currentPage } });
  }
}
